import React from "react";

/*
 * Service icons.
 *
 * Drawn rather than picked from an icon font: the four services are specific
 * enough that a stock glyph set has nothing for three of them, which is how
 * the page ended up with a chat bubble, a vector-pen, a grid of squares and a
 * sunrise. Each mark here names its own subject —
 *
 *   conversation  a speech bubble around a heart: the counselling session
 *   pedigree      the standard pedigree notation — square for male, circle
 *                 for female, joined by a mating line, descending to two
 *                 children. The service's own copy is about reading these.
 *   helix         a DNA double helix: the thing carrier screening reads
 *   scan          an ultrasound sweep: screening during a pregnancy
 *
 * All four are on a 24px grid at a 1.5px stroke with rounded caps, which is
 * the logo mark's own line language, so they read as part of the brand rather
 * than as a borrowed set. They inherit colour from the element they sit in.
 */
const ICONS = {
  conversation: (
    <>
      <rect x="2.5" y="3.5" width="19" height="14" rx="3.5" />
      <path d="M8 17.5v3.3l4.1-3.3" />
      <path d="M12 14.1s-3.5-2.1-3.5-4.3a2 2 0 0 1 3.5-1.2 2 2 0 0 1 3.5 1.2c0 2.2-3.5 4.3-3.5 4.3z" />
    </>
  ),
  pedigree: (
    <>
      <rect x="3.25" y="3" width="5" height="5" rx="0.6" />
      <circle cx="18.25" cy="5.5" r="2.5" />
      <path d="M8.25 5.5h7.5" />
      <path d="M12 5.5V10" />
      <path d="M7.5 10h9" />
      <path d="M7.5 10v2.5M16.5 10v2.5" />
      <circle cx="7.5" cy="15" r="2.5" />
      <rect x="14" y="12.5" width="5" height="5" rx="0.6" />
    </>
  ),
  helix: (
    <>
      <path d="M8 2.5C8 7 16 7 16 12s-8 5-8 9.5" />
      <path d="M16 2.5C16 7 8 7 8 12s8 5 8 9.5" />
      <path d="M8.7 4.6h6.6M8 12h8M8.7 19.4h6.6" />
    </>
  ),
  /* The transducer and the sector of image below it. Two earlier attempts
     drew something else entirely: concentric arcs from a corner are exactly
     the wifi glyph, and a narrow cone joined to the probe is a lab flask.
     A wide, shallow sector set apart from the probe is what reads as a scan. */
  scan: (
    <>
      <rect x="9.5" y="2.5" width="5" height="2.4" rx="0.9" />
      <path d="M10.1 6.6 3.6 14.6a10.8 10.8 0 0 0 16.8 0l-6.5-8Z" />
      <circle cx="12" cy="12" r="1.5" />
    </>
  ),
};

// Drawing the mark, one stroke at a time.
//
// `pathLength="1"` tells the browser to treat a shape as one unit long for the
// purposes of dashing, whatever its real geometry, and to scale the dash pattern
// to fit. That is what lets a single CSS rule — dasharray 1, dashoffset 1 -> 0 —
// draw a rounded rect, a circle and a nine-segment path alike. The alternative
// is getTotalLength() per element in JS: a geometry read per icon, a DOM
// mutation to write the result back, and no implementation at all on <rect> or
// <circle> in older engines, which is three of the four marks here.
//
// `--draw-index` is the order the strokes are laid down in, which for the
// pedigree is the order the notation is read: the square, then the circle, then
// the line joining them, then down to the children.
//
// The dashed state lives in _motion.scss rather than here, keyed to an
// ancestor's data-reveal attribute or, for the service hero, to a mount
// animation. Keying it to this class alone would leave an icon whose observer
// never ran rendered blank instead of simply drawn.
const withDrawOrder = (children) =>
  React.Children.map(children, (child, i) =>
    React.cloneElement(child, { pathLength: 1, style: { "--draw-index": i } })
  );

function ServiceIcon({ name, size = 24, className, draw = false }) {
  const icon = ICONS[name];
  if (!icon) return null;

  return (
    <svg
      className={
        draw ? [className, "service-icon--draw"].filter(Boolean).join(" ") : className
      }
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {draw ? withDrawOrder(icon.props.children) : icon}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(ICONS);

export default ServiceIcon;
